import { Alert, Box, Button, Code, Stack, Text } from '@citibox-panel/ui';
import { useReportFailure } from '../../search-shell/hooks/useErrorNotice';
import type { DeliveryActionState } from '../model/deliveryActionFlow';

export interface ConfirmSequenceControlProps {
  // Its OWN flow instance, not the «Acciones» card's: this control hangs off a carril, so the two
  // are physically separate and share nothing but the reducer function. `action` here is either
  // null or 'confirmSequence'.
  state: DeliveryActionState;
  // The code the big slot is holding — what the agent will read out after confirming.
  nextCode: string;
  onStart: () => void;
  onCancel: () => void;
  onConfirm: () => void;
  // Fired once the failure has been reported to the shell's notice, so whoever owns the flow can
  // put it back to idle: the trigger comes straight back while the notice stays up until the agent
  // dismisses it. There is no dismiss affordance HERE — the notice carries its own «X» (ADR 0021).
  onFailureReported: () => void;
}

// TODO: i18n — copy en ES hasta Fase 1.
const TRIGGER_LABEL = 'Confirmar secuencia';
const ERROR_MESSAGE = 'No se ha podido confirmar la secuencia.';
const NOTICE_ID = 'confirm-sequence';

// «Confirmar secuencia», hung off the open carril — next to the sequence it moves, which is the
// whole argument for this placement over a modal: the agent is squaring the strip against a physical
// cerradura, and a dialog over the log takes away the thing they are checking against.
//
// One phase visible at a time, so the control occupies one slot at the foot of the carril and never
// stacks: the trigger GIVES WAY to the confirm panel, and the panel to its banner. That is also why
// focus is managed below — the trigger the agent just pressed stops existing.
//
// The error copy is panel-owned and generic. Devices' own codes are code-agnostic for the panel
// (every failure is «try again»), and its `message`/`detail` never reach a sentence an agent might
// read to a «Usuario».
export function ConfirmSequenceControl({
  state,
  nextCode,
  onStart,
  onCancel,
  onConfirm,
  onFailureReported,
}: ConfirmSequenceControlProps) {
  const { phase } = state;
  // The failure floats instead of taking the carril's slot (ADR 0021). Code-agnostic on purpose:
  // every devices failure is «try again», so the copy is one sentence and the backend's own
  // `message`/`detail` never reaches it.
  useReportFailure(NOTICE_ID, phase === 'error' ? ERROR_MESSAGE : null, onFailureReported);

  if (phase === 'idle') {
    return (
      <Button variant="contained" size="small" onClick={onStart}>
        {TRIGGER_LABEL}
      </Button>
    );
  }

  if (phase === 'confirming' || phase === 'pending') {
    const pending = phase === 'pending';
    return (
      <Box
        sx={{
          border: 1,
          borderColor: 'divider',
          borderRadius: 2,
          bgcolor: 'background.default',
          p: 1.5,
        }}
      >
        <Stack spacing={1}>
          {/* A status, not a label: it names the credential being committed and it changes with
              every step of the slide. */}
          <Box role="status">
            <Text variant="body2" component="p">
              El siguiente código a dar será <Code>{nextCode}</Code>.
            </Text>
          </Box>
          <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
            <Button variant="text" onClick={onCancel} disabled={pending}>
              Cancelar
            </Button>
            {/* The panel replaced the trigger the agent just pressed, so focus has to land
                somewhere deliberate. Mount-only, which is exactly «on the way in» — a re-focus on
                every render would fight an agent who tabbed to «Cancelar». */}
            <Button autoFocus variant="contained" loading={pending} onClick={onConfirm}>
              Confirmar
            </Button>
          </Stack>
        </Stack>
      </Box>
    );
  }

  // Success stays IN PLACE: ADR 0021 is about errors, and a confirmation belongs next to the thing
  // it confirms.
  if (phase === 'success') {
    return <Alert severity="success">Secuencia confirmada</Alert>;
  }

  // Nothing for the failure: it was reported above and the flow is on its way back to idle, so the
  // slot is left to the trigger rather than to a banner nobody can act on.
  return null;
}
